/**
 * Admin → Monday mail — the coming Monday email, person by person (#1500).
 *
 * Built by the code that will send it, as of the next send, and not sent: the
 * server dispatches, stamps and marks nothing. Before this, the only way to
 * know who would get Monday's mail was an ops workflow run.
 *
 * The bodies hold real titles and names, which is why it is admin-only, and
 * they render in a sandboxed frame after DOMPurify: no script, no navigation.
 */

import { useMemo, useState, type ReactElement } from 'react'
import DOMPurify from 'dompurify'
import {
  Badge,
  Caption1,
  Spinner,
  Switch,
  Text,
  Title3,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  Clock16Regular,
  HourglassHalf20Regular,
  MailClock20Regular,
  News20Regular,
  PeopleQueue20Regular,
} from '@fluentui/react-icons'
import { useMondayMail } from '../hooks/useAdminUsers'
import type { MondayMail, MondayMailPart } from '../types/admin'
import { ErrorCard } from '../components/ErrorCard'
import { InfoHint } from '../components/planning/PlanParts'
import { apiErrorMessage } from '../lib/apiError'

const PART_ICON: Record<MondayMailPart['key'], ReactElement> = {
  stalled: <HourglassHalf20Regular aria-hidden="true" />,
  lapsed: <PeopleQueue20Regular aria-hidden="true" />,
  digest: <News20Regular aria-hidden="true" />,
}

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  when: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
  },
  parts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalS,
  },
  part: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  partIcon: { display: 'flex', color: tokens.colorBrandForeground1 },
  partLabel: { flexGrow: 1 },
  counts: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  body: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
    '@media (max-width: 760px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    maxHeight: '70vh',
    overflowY: 'auto',
  },
  person: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    textAlign: 'left',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `1px solid transparent`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  picked: {
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  personLine: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  preview: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  frame: {
    width: '100%',
    height: '70vh',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#ffffff',
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground3,
  },
})

function partState(p: MondayMailPart): [string, 'success' | 'warning' | 'subtle'] {
  if (!p.on) return ['Off', 'subtle']
  if (p.paused) return ['Paused this week', 'warning']
  return ['On', 'success']
}

function Person({
  mail,
  labels,
  picked,
  onPick,
}: {
  mail: MondayMail
  labels: Record<string, string>
  picked: boolean
  onPick: () => void
}) {
  const styles = useStyles()
  const bellOnly = !mail.subject
  return (
    <button
      type="button"
      aria-pressed={picked}
      className={mergeClasses(styles.person, picked && styles.picked)}
      onClick={onPick}
    >
      <Text weight="semibold">{mail.name || mail.email}</Text>
      <span className={styles.personLine}>
        <Caption1>{mail.role}</Caption1>
        {mail.parts.map((k) => (
          <span key={k} role="img" aria-label={labels[k]} title={labels[k]}>
            {PART_ICON[k]}
          </span>
        ))}
        {mail.not_mailed ? (
          <Badge size="small" appearance="tint" color="warning">
            {mail.not_mailed}
          </Badge>
        ) : (
          bellOnly && (
            <Badge size="small" appearance="tint" color="subtle">
              Bell only
            </Badge>
          )
        )}
      </span>
    </button>
  )
}

export function AdminMondayMail() {
  const styles = useStyles()
  const [includeOff, setIncludeOff] = useState(false)
  const [pickedId, setPickedId] = useState<number | null>(null)
  const { data, isLoading, error } = useMondayMail(includeOff)

  const mails = useMemo(() => data?.mails ?? [], [data])
  const labels = useMemo(
    () => Object.fromEntries((data?.parts ?? []).map((p) => [p.key, p.label])),
    [data],
  )
  const picked = mails.find((m) => m.user_id === pickedId) ?? mails.find((m) => m.subject) ?? null
  const html = useMemo(
    () => (picked?.html ? DOMPurify.sanitize(picked.html, { WHOLE_DOCUMENT: true }) : ''),
    [picked],
  )

  if (isLoading) return <Spinner label="Building the coming mail…" />
  if (error) return <ErrorCard message={apiErrorMessage(error, 'Could not build the preview')} />
  if (!data) return null

  const sendAt = new Date(data.send_at).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
  const someOff = data.parts.some((p) => !p.on || p.paused)
  const emails = mails.filter((m) => m.subject && !m.not_mailed).length
  const bellOnly = mails.filter((m) => !m.subject).length
  const notMailed = mails.filter((m) => m.subject && m.not_mailed).length

  return (
    <div className={styles.page}>
      <div>
        <div className={styles.head}>
          <Title3 as="h1">Monday mail</Title3>
          <InfoHint text="Built by the code that sends it, as of the next send. Nothing is sent from this page." />
        </div>
        <Caption1 className={styles.when}>
          <Clock16Regular aria-hidden="true" />
          Sends {sendAt}, 08:00 Central European time
        </Caption1>
      </div>

      <div className={styles.parts}>
        {data.parts.map((p) => {
          const [state, color] = partState(p)
          return (
            <div key={p.key} className={styles.part}>
              <span className={styles.partIcon}>{PART_ICON[p.key]}</span>
              <Text className={styles.partLabel}>{p.label}</Text>
              <Badge size="small" appearance="tint" color={color}>
                {state}
              </Badge>
            </div>
          )
        })}
      </div>

      {someOff && (
        <Switch
          checked={includeOff}
          onChange={(_, d) => setIncludeOff(d.checked)}
          label="Show the parts that are off"
        />
      )}

      <div className={styles.counts}>
        <Badge appearance="tint" color="brand">
          {emails} email{emails === 1 ? '' : 's'}
        </Badge>
        <Badge appearance="tint" color="subtle">
          {bellOnly} bell only
        </Badge>
        {notMailed > 0 && (
          <Badge appearance="tint" color="warning">
            {notMailed} not mailed
          </Badge>
        )}
      </div>

      {mails.length === 0 ? (
        <Text className={styles.empty}>
          <MailClock20Regular aria-hidden="true" />
          Nobody gets a Monday mail this week.
        </Text>
      ) : (
        <div className={styles.body}>
          <nav className={styles.list} aria-label="Recipients">
            {mails.map((m) => (
              <Person
                key={m.user_id}
                mail={m}
                labels={labels}
                picked={picked?.user_id === m.user_id}
                onPick={() => setPickedId(m.user_id)}
              />
            ))}
          </nav>
          <div className={styles.preview}>
            {picked?.subject ? (
              <>
                <Text weight="semibold">{picked.subject}</Text>
                <iframe
                  title={`Email to ${picked.name || picked.email}`}
                  className={styles.frame}
                  sandbox=""
                  srcDoc={html}
                />
              </>
            ) : (
              <Text className={styles.empty}>
                <MailClock20Regular aria-hidden="true" />
                Bell only: no email for this person.
              </Text>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
